/* eslint-disable max-lines */
import { useNavigate } from 'react-router-dom';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';

import { useFormik } from 'formik';
import { useRecoilValue } from 'recoil';

import { createExternalInvoices } from 'api/Billing';

import useModal from 'lib/hooks/useModal';
import { formatDate } from 'lib/helpers/dateUtils';
import { hasModule, ia, iaRa, inRn } from 'lib/helpers/utility';
import { useCustomMutation } from 'lib/hooks/useCustomMutation';
import { useTerminal } from 'lib/hooks/queries/terminals/useTerminal';
import { overviewLedgerQueryKeys } from 'lib/constants/queries/queryKeys';

import { modulesState } from 'components/state';
import { currentPractice } from 'components/practice/practiceState';
import { getRecurringValidationError } from 'components/Payments/NewPayment/PaymentContent/components/RecurringPayments/lib/helpers';

import { getMutationFn } from './utils';

export const PaymentContext = createContext();
export const PaymentContextApi = createContext();

const DEFAULT_SEND_INVOICE = { dueDate: null, memo: '' };
const DEFAULT_INVOICES = [];

export const CREATE_PATIENT_ALLOWED_METHODS = ['credit-card', 'ach'];

export const PaymentContextProvider = ({
  children,
  amount,
  surchargeAmount = 0,
  initialMethod,
  invoiceToken,
  patientId,
  appointmentId,
  invoices = DEFAULT_INVOICES,
  practiceId: practiceIdProp,
  onSuccess = () => {},
  onError = () => {},
  checkin = false,
  recurringPayment = null,
  forceSaveCard = false,
  fromPlans = false,
  createPatientFromCCInfo = false,
  enabled = true
}) => {
  const navigate = useNavigate();
  const practice = useRecoilValue(currentPractice);
  const practiceModules = useRecoilValue(modulesState);
  const practiceId = practiceIdProp || practice.id;
  // Technology fee only applies when its module is enabled. In checkin the patient flow
  // has no modulesState, so surcharge is gated upstream (SecurePaymentContent) instead.
  const techFeeEnabled = checkin || hasModule(practiceModules, 'medipay.technology_fee');
  const [postedResponse, setPostedResponse] = useState(null);
  const [isSendingInvoice, setIsSendingInvoice] = useState(false);
  const formikValuesRef = useRef(null);
  const processPaymentRef = useRef(null);
  const processorRef = useRef(null);
  const terminalIdRef = useRef(null);
  const [selectedTerminal, setSelectedTerminal] = useState(null);

  const { isOpen, openModal, closeModal } = useModal(false);

  const initialValues = useMemo(
    () => ({
      amount,
      appliedBalance: 0,
      method: initialMethod,
      methodDetail: null,
      invoices,
      appointmentId,
      surchargeAmount,
      patientId,
      payerId: null,
      invoiceToken,
      saveCard: forceSaveCard,
      paymentOption: null,
      surchargeEnabled: techFeeEnabled && practice?.surcharge_enabled,
      customPaymentAmounts: {},
      recurringPayment,
      checkoutMode: recurringPayment ? 'recurring-plan' : 'pay-now',
      sendInvoice: DEFAULT_SEND_INVOICE
    }),
    [patientId, practiceId, appointmentId, invoiceToken, amount, surchargeAmount]
  );

  const formik = useFormik({
    initialValues,
    enableReinitialize: true
  });

  formikValuesRef.current = formik.values;

  const { data: terminalData, isLoading: isTerminalLoading } = useTerminal({
    params: { practiceId, patientId, attribute: 'online' },
    dependencies: [practiceId, patientId],
    options: {
      enabled: enabled && !!practiceId
    }
  });

  const isKiosk = !!localStorage.getItem('myriadIsKiosk');

  useEffect(() => {
    if (recurringPayment) {
      formik.setFieldValue('method', 'credit-card');
    }
  }, [recurringPayment]);

  useEffect(() => {
    if (techFeeEnabled && practice?.surcharge_enabled) {
      formik.setFieldValue('surchargeEnabled', true);
    }
  }, [techFeeEnabled, practice?.surcharge_enabled]);

  useEffect(() => {
    const defaultForCard =
      terminalData?.cardPresentTerminal || terminalData?.onlineTerminal || null;
    const defaultForOnline =
      terminalData?.onlineTerminal || terminalData?.cardPresentTerminal || null;

    setSelectedTerminal((current) => {
      const isCardMethod = formik.values.method === 'card';
      const defaultTerminal = isCardMethod ? defaultForCard : defaultForOnline;
      const currentValidForMethod = current && (isCardMethod ? !current.online : current.online);
      return currentValidForMethod ? current : defaultTerminal;
    });
  }, [terminalData?.onlineTerminal, terminalData?.cardPresentTerminal, formik.values.method]);

  const terminals = iaRa(terminalData?.terminals).filter((t) => t.online);
  const cardPresentTerminals = iaRa(terminalData?.terminals).filter((t) =>
    t.processor === 'elavon' ? !t.online : true
  );
  const terminalPin = selectedTerminal?.pin;
  const terminalId = selectedTerminal?.id;
  const processor = selectedTerminal?.processor;
  const isFullAppliedBalance = formik.values.appliedBalance > 0 && formik.values.amount <= 0;
  const appliedMethod = isFullAppliedBalance ? 'applied-balance' : formik.values.method;

  terminalIdRef.current = terminalId;

  processPaymentRef.current = getMutationFn(processor, appliedMethod);
  processorRef.current = processor;

  const invalidateQueryKey = [
    'patientBalance',
    'plans',
    ['cards', patientId],
    ...overviewLedgerQueryKeys
  ];

  const { mutateAsync, isPending: isCharging } = useCustomMutation({
    mutationFn: (params) => processPaymentRef.current(navigate, params),
    disableAlert: true,
    invalidateQueryKey,
    onSuccess: (params) => {
      const recurring = formikValuesRef.current?.recurringPayment;
      const method = formikValuesRef.current?.method;
      if (recurring && method === 'request-payment' && !params?.instantPacketToken) {
        onError(params?.error || "Couldn't create the pre-authorization link", params?.code);
        return;
      }
      if (method === 'defer-payment') {
        onSuccess({ ...params, deferred: true, method }, isFullAppliedBalance);
        return;
      }
      onSuccess(
        recurring
          ? {
              ...params,
              recurring: true,
              recurringType: recurring.type,
              recurringPayment: recurring,
              method,
              preAuthRequired: recurring.preAuthRequired !== false,
              patientId:
                formikValuesRef.current?.patientId || patientId || params?.patient_id || null,
              cardId: params?.card_id || params?.cardId || null,
              instantPacketToken: params?.instantPacketToken || null
            }
          : params,
        isFullAppliedBalance
      );
    },
    handleResponse: onError
  });

  const handlePayment = useCallback(
    async (data) => {
      try {
        if (formikValuesRef.current?.checkoutMode === 'send-invoice') {
          const sendInvoiceData = formikValuesRef.current?.sendInvoice || {};
          const invoices = iaRa(formikValuesRef.current?.invoices);
          const total = invoices.reduce(
            (sum, inv) =>
              sum + Number(inv?.balance ?? inv?.total_amount_cents ?? inv?.amount_cents ?? 0),
            0
          );
          if (invoices.length === 0 || total <= 0) {
            onError('No invoices selected or total is zero', 1);
            return { code: 1 };
          }
          const internalDetails = invoices.map(({ id, balance }) => ({
            id,
            amount_paid: balance
          }));
          const surchargeAmount = inRn(formikValuesRef.current?.surchargeAmount);
          const surchargeEnabled = !!formikValuesRef.current?.surchargeEnabled;
          const effectiveSurcharge = surchargeEnabled ? surchargeAmount : 0;
          const externalInvoicePayload = {
            invoices: [
              {
                amount_cents: total,
                total_amount_cents: total + effectiveSurcharge,
                due_date:
                  sendInvoiceData.dueDate ||
                  formatDate(new Date(), practice?.timezone, 'MM/dd/yyyy'),
                memo: sendInvoiceData.memo || '',
                internal_invoice_ids: invoices.map((i) => i.id),
                patient: { id: formikValuesRef.current?.patientId || patientId },
                tax_percentage: 0,
                surcharge_amount: effectiveSurcharge,
                aeob: false,
                is_detailed: false,
                type: 'kiosk',
                internal_details: internalDetails
              }
            ],
            skipConfirmation: true
          };

          setIsSendingInvoice(true);
          try {
            const result = await createExternalInvoices(navigate, externalInvoicePayload);
            if (result?.code !== 0) {
              onError(result?.error, result?.code);
              return result;
            }
            const externalInvoice = result?.invoices?.[0] || null;

            onSuccess({ sendInvoice: true, externalInvoice }, false);
            return { code: 0, externalInvoice };
          } finally {
            setIsSendingInvoice(false);
          }
        }

        if (!processPaymentRef.current) {
          console.warn(
            `No payment handler resolved (practiceId=${practiceId}, processor=${
              processorRef.current || 'none'
            }, method=${formikValuesRef.current?.method || 'none'})`
          );
          onError('Payment could not be processed. Please try again.', 1);
          return { code: 1 };
        }

        const finalValues = Object.fromEntries(
          Object.entries(formikValuesRef.current).filter(([_, v]) => v !== undefined && v !== null)
        );

        const recurring = formikValuesRef.current?.recurringPayment;
        const fullAmount = Number(formikValuesRef.current?.amount) || 0;
        const upfrontOverride =
          recurring?.type === 'installments' && Number(recurring?.upfrontPayment) > 0
            ? Number(recurring.upfrontPayment)
            : null;

        const recurringPayload = recurring
          ? {
              ...recurring,
              amount: fullAmount
            }
          : recurring;

        const selectedMethod = formikValuesRef.current?.method;
        const methodSupportsCreatePatient = CREATE_PATIENT_ALLOWED_METHODS.includes(selectedMethod);

        const canAttachToPatient =
          Boolean(finalValues.patientId) ||
          (methodSupportsCreatePatient && createPatientFromCCInfo);

        const response = await mutateAsync({
          token: data?.token,
          ...finalValues,
          ...(recurringPayload && { recurringPayment: recurringPayload }),
          ...data,
          ...(upfrontOverride !== null && { amount: upfrontOverride }),
          ...(methodSupportsCreatePatient &&
            createPatientFromCCInfo && { createPatientFromCCInfo: true }),
          terminalId: terminalIdRef.current,
          practiceId,
          checkin,
          ...(!canAttachToPatient && { saveCard: false, recurringPayment: null })
        });

        setPostedResponse(response);

        if (response?.code !== 0) {
          closeModal();
        }

        return response;
      } catch (error) {
        console.error(error);
        closeModal();
        onError(true);
      }
    },
    [mutateAsync, practiceId, checkin, onError, onSuccess, createPatientFromCCInfo]
  );

  const togglePaymentMethod = useCallback(
    (key) => {
      const isSameMethod = formik.values.method === key;
      const nextMethod = isSameMethod ? null : key;
      formik.setFieldValue('methodDetail', null);
      formik.setFieldValue('method', nextMethod);

      const recurring = formik.values.recurringPayment;
      if (recurring && nextMethod === 'request-payment') {
        formik.setFieldValue('recurringPayment', { ...recurring, preAuthRequired: true });
      }

      closeModal();
    },
    [formik.values.method, formik.values.recurringPayment, formik.setFieldValue, closeModal]
  );

  const isLoading = isTerminalLoading || isCharging || isSendingInvoice;

  const isAmountLessThanAppliedBalance =
    formik.values.amount <= 0 && formik.values.appliedBalance <= 0;

  const isSendInvoice = formik.values.checkoutMode === 'send-invoice';
  const isRequestPayment = formik.values.method === 'request-payment';

  const isSendInvoiceEmpty = isSendInvoice && !ia(formik.values.invoices);
  const hasValidPaymentMethod = formik.values.method || isFullAppliedBalance || isSendInvoice;

  const isSavedCardWithoutDetail =
    formik.values.method === 'saved-cards' && !formik.values.methodDetail;

  const recurringValidationError = getRecurringValidationError(
    formik.values.recurringPayment,
    formik.values.amount
  );

  const isDisabled = isSendInvoice
    ? isLoading || isSendInvoiceEmpty
    : isRequestPayment
      ? isLoading ||
        !processPaymentRef.current ||
        isAmountLessThanAppliedBalance ||
        !!recurringValidationError
      : !hasValidPaymentMethod ||
        isLoading ||
        !processPaymentRef.current ||
        isAmountLessThanAppliedBalance ||
        isSavedCardWithoutDetail ||
        !!recurringValidationError;

  const disabledTooltip =
    (isSendInvoiceEmpty && 'Add at least one invoice with a positive amount') ||
    (isAmountLessThanAppliedBalance && 'Amount must be greater than 0') ||
    (!isSendInvoice && !appliedMethod && 'Please select a payment method') ||
    (!isSendInvoice && !processPaymentRef.current && 'Payment method not supported') ||
    (isSavedCardWithoutDetail && 'Please select a saved card') ||
    recurringValidationError ||
    (isLoading && 'Processing payment…') ||
    '';

  const contextValue = useMemo(
    () => ({
      values: formik.values,
      isLoading,
      isDisabled,
      disabledTooltip,
      processor,
      terminalPin,
      terminalId,
      postedResponse,
      isOpen,
      isTerminalLoading,
      isCharging,
      terminal: selectedTerminal,
      isFullAppliedBalance,
      practiceId,
      terminals,
      terminalData,
      isKiosk,
      cardPresentTerminals,
      forceSaveCard,
      fromPlans,
      createPatientFromCCInfo
    }),
    [
      formik.values,
      isLoading,
      isDisabled,
      disabledTooltip,
      processor,
      terminalPin,
      terminalId,
      postedResponse,
      isOpen,
      isTerminalLoading,
      isCharging,
      selectedTerminal,
      isFullAppliedBalance,
      practiceId,
      terminals,
      terminalData,
      isKiosk,
      cardPresentTerminals,
      forceSaveCard,
      fromPlans,
      createPatientFromCCInfo
    ]
  );

  const contextApi = useMemo(
    () => ({
      togglePaymentMethod,
      setFieldValue: formik.setFieldValue,
      handlePayment,
      openModal,
      closeModal,
      setSelectedTerminal
    }),
    [togglePaymentMethod, handlePayment, openModal, closeModal, setSelectedTerminal]
  );

  return (
    <PaymentContext.Provider value={contextValue}>
      <PaymentContextApi.Provider value={contextApi}>{children}</PaymentContextApi.Provider>
    </PaymentContext.Provider>
  );
};

export const usePaymentContext = () => useContext(PaymentContext);
export const usePaymentContextApi = () => useContext(PaymentContextApi);
